import type {
    CreatePaymentKassviaModel,
    CreatePaymentRevolutModel,
    OrganizationInfoModel,
} from '@models';
import type { OperationBalanceStatusEnum } from '@models/enums';
import { DashboardVariantEnum, OrganizationRoleEnum } from '@models/enums';
import type { OperationBalanceModel } from '@models/operation/operation-balance-model';
import type { DetailsFormValues } from '@modules/auth/forms/form-details/types';
import type { AuthUserInfo } from '@modules/auth/store';
import { dashboardAccountApiSlice } from '@modules/dashboard/api/dashboard-account-api';
import { operationApiSlice } from '@modules/dashboard/api/operation-api';
import { organizationsApiSlice } from '@modules/dashboard/api/organizations-api';
import { paymentApiSlice } from '@modules/dashboard/api/payment-api';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, isAnyOf } from '@reduxjs/toolkit';

export const INIT_DEPOSIT = 1000;

export type SessionState = {
    organizationId: string;
    requestedOrganizationId: string;
    organizationsUpdateDate: string;
    token: string;
    revolutPayment: Partial<CreatePaymentRevolutModel> & {
        amount?: number;
    };
    kassviaPayment: {
        data?: CreatePaymentKassviaModel;
        amount?: number;
    };
    balanceStatus?: OperationBalanceStatusEnum;
    balance: number;
    organizationsList: OrganizationInfoModel[];
    organizationsOwnedCount: number;
    organizationInfo?: OrganizationInfoModel;
    organizationCreatedDate?: string;
    billingContractDueDate?: string;
    hasOperations?: boolean;
    hasDeposits?: boolean;
    isSandbox: boolean;
    userId: string;
    dashboardVariantId: DashboardVariantEnum;
    requestedDashboardVariantId?: DashboardVariantEnum;
    // used when user fills out the form in onboarding, but registered through Google
    questionnaireData?: DetailsFormValues;
};

const initialState: SessionState = {
    organizationId: '',
    userId: '',
    requestedOrganizationId: localStorage.getItem('dashboard-organizationId') || '',
    organizationsUpdateDate: new Date().getTime().toString(),
    token: '',
    balanceStatus: undefined,
    balance: 0,
    revolutPayment: {
        amount: undefined,
        id: undefined,
        expires_at: undefined,
        items: undefined,
    },
    kassviaPayment: {},
    organizationInfo: undefined,
    organizationsList: [],
    organizationsOwnedCount: 0,
    hasOperations: undefined,
    hasDeposits: undefined,
    billingContractDueDate: undefined,
    isSandbox: localStorage.getItem('dashboard-is-sandbox') === 'true',
    questionnaireData: undefined,
    dashboardVariantId: (() => {
        const value = localStorage.getItem('dashboard-variantId');

        return value && Object.values(DashboardVariantEnum).includes(value as DashboardVariantEnum)
            ? (value as DashboardVariantEnum)
            : DashboardVariantEnum.KYC;
    })(),
    requestedDashboardVariantId: undefined,
};

export const sessionSlice = createSlice({
    name: 'dashboardSessionSlice',
    initialState,
    reducers: {
        clearSession: (state) => {
            localStorage.clear();
            localStorage.setItem('dashboard-organizationId', state.organizationId);
            localStorage.setItem('dashboard-variantId', state.dashboardVariantId);

            return {
                ...initialState,
                requestedOrganizationId: state.organizationId,
                dashboardVariantId: state.dashboardVariantId,
            };
        },
        setOrganizationInfo: (state, { payload }: PayloadAction<OrganizationInfoModel>) => {
            state.organizationInfo = payload;

            if (payload.flavor === 'ccd') {
                localStorage.setItem('dashboard-variantId', DashboardVariantEnum.CCD);
                state.dashboardVariantId = DashboardVariantEnum.CCD;
                state.requestedDashboardVariantId = undefined;
            } else if (payload.flavor === 'blt') {
                localStorage.setItem('dashboard-variantId', DashboardVariantEnum.BLT);
                state.dashboardVariantId = DashboardVariantEnum.BLT;
                state.requestedDashboardVariantId = undefined;
            } else if (
                state.dashboardVariantId === DashboardVariantEnum.CCD ||
                state.dashboardVariantId === DashboardVariantEnum.BLT
            ) {
                localStorage.setItem('dashboard-variantId', DashboardVariantEnum.KYC);
                state.dashboardVariantId = DashboardVariantEnum.KYC;
                state.requestedDashboardVariantId = undefined;
            }
        },
        setRequestedOrganizationId: (state, { payload }: PayloadAction<string>) => {
            state.requestedOrganizationId = payload;
        },
        setOrganizationId: (state, { payload }: PayloadAction<string>) => {
            localStorage.setItem('dashboard-organizationId', payload);
            state.organizationId = payload;
        },
        setOrganizationsList: (state, action: PayloadAction<OrganizationInfoModel[]>) => {
            state.organizationsList = action.payload;
            let count = 0;

            if (Array.isArray(state.organizationsList) && state.organizationsList.length) {
                // eslint-disable-next-line no-restricted-syntax
                for (const organization of state.organizationsList) {
                    if (organization.organization_role === OrganizationRoleEnum.OWNER) {
                        count += 1;
                    }
                }
            }
            state.organizationsOwnedCount = count;
        },
        setIsSandbox: (state, { payload }: PayloadAction<boolean>) => {
            state.isSandbox = payload;
            localStorage.setItem('dashboard-is-sandbox', payload.toString());
        },
        setRevolutPaymentAmount: (state, { payload }: PayloadAction<number>) => {
            state.revolutPayment.amount = payload;
        },
        setKassviaPaymentAmount: (state, { payload }: PayloadAction<number>) => {
            state.kassviaPayment.amount = payload;
        },
        clearKassviaPayment: (state) => {
            state.kassviaPayment = {};
        },
        clearRevolutPayment: (state) => {
            state.revolutPayment = {
                amount: undefined,
                id: undefined,
                expires_at: undefined,
                items: undefined,
            };
        },
        setQuestionnaireData: (
            state,
            { payload }: PayloadAction<DetailsFormValues | undefined>,
        ) => {
            state.questionnaireData = payload;
        },
        setRequestedDashboardVariantId: (
            state,
            { payload }: PayloadAction<DashboardVariantEnum>,
        ) => {
            state.requestedDashboardVariantId = payload;
        },
        setDashboardVariantId: (state, { payload }: PayloadAction<DashboardVariantEnum>) => {
            localStorage.setItem('dashboard-variantId', payload);
            state.dashboardVariantId = payload;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            organizationsApiSlice.endpoints.getInitialOrganizationInfo.matchFulfilled,
            (state, action) => {
                if (action.payload) {
                    sessionSlice.caseReducers.setOrganizationId(state, {
                        ...action,
                        payload: action.payload.organization_id,
                    });
                    sessionSlice.caseReducers.setOrganizationInfo(state, action);
                }
            },
        );

        builder.addMatcher(
            isAnyOf(
                organizationsApiSlice.endpoints.createOrganization.matchFulfilled,
                organizationsApiSlice.endpoints.updateOrganization.matchFulfilled,
                organizationsApiSlice.endpoints.acceptInvitation.matchFulfilled,
                organizationsApiSlice.endpoints.leaveFromOrganization.matchFulfilled,
            ),
            (state) => {
                state.organizationsUpdateDate = new Date().getTime().toString();
            },
        );

        builder.addMatcher(
            isAnyOf(
                dashboardAccountApiSlice.endpoints.getUserInfo.matchFulfilled,
                dashboardAccountApiSlice.endpoints.googleLogin.matchFulfilled,
                dashboardAccountApiSlice.endpoints.login.matchFulfilled,
                dashboardAccountApiSlice.endpoints.completeTwoFALogin.matchFulfilled,
            ),

            (state, action) => {
                if (!action.payload) {
                    return;
                }
                const userInfo = action.payload as AuthUserInfo;

                if (state.userId && userInfo?.account?.account_id !== state.userId) {
                    // need to update org list and reinit app
                    state.organizationsUpdateDate = new Date().getTime().toString();
                }

                if (userInfo?.account?.account_id) {
                    state.userId = userInfo.account.account_id as string;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(
                operationApiSlice.endpoints.getInitialBalance.matchFulfilled,
                operationApiSlice.endpoints.getBalance.matchFulfilled,
            ),

            (state, action: PayloadAction<OperationBalanceModel>) => {
                if (!action.payload) {
                    return;
                }
                const balanceData = action.payload;

                state.hasOperations = balanceData.created_at !== balanceData.updated_at;
                state.hasDeposits = state.hasOperations || balanceData.balance > INIT_DEPOSIT;
                state.balanceStatus = balanceData.status;
                state.organizationCreatedDate = balanceData.created_at;
            },
        );

        builder.addMatcher(
            operationApiSlice.endpoints.getRealTimeBalance.matchFulfilled,
            (state, action: PayloadAction<OperationBalanceModel>) => {
                state.balance = action?.payload?.balance || 0;
            },
        );

        builder.addMatcher(
            paymentApiSlice.endpoints.createPaymentRevolut.matchFulfilled,
            (state, action) => {
                if (action.payload != null) {
                    state.revolutPayment.id = action.payload.id;
                    state.revolutPayment.expires_at = action.payload.expires_at;
                    state.revolutPayment.items = action.payload.items;
                }
            },
        );

        builder.addMatcher(
            paymentApiSlice.endpoints.createPaymentKassvia.matchFulfilled,
            (state, action: PayloadAction<CreatePaymentKassviaModel>) => {
                if (action.payload != null) {
                    state.kassviaPayment.data = action.payload;
                }
            },
        );
    },
});
